// src/Views/StoreDetail.js
import React, { useContext } from 'react';
import { StoreContext } from '../contexts/StoreContext';
import { COLORS } from '../constants/colors'; // adjust the path as needed
import { MAX_CONTENT_WIDTH } from '../constants/layout';
import {
  CODA_EXTRABOLD_DISPLAY_SIZE,
  CODA_EXTRABOLD_MOBILE_SIZE,
} from '../constants/typography';
import { formatAddress } from '../utils/address';
import {
  formatBusinessHoursRows,
  getStoreLocalTimeParts,
  to12h,
  windowsForWeekday,
} from '../utils/businessHours';
import usePageView from '../hooks/usePageView';

export default function StoreDetail() {
  usePageView('contact');
  const { store, loading } = useContext(StoreContext);
  const formattedAddress = formatAddress(store?.location);

  const findNextOpen = (businessHours) => {
    const { timeZone, daily } = businessHours;
    const { weekday, minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const todayWindows = windowsForWeekday(daily, weekday).map((w) => {
      const [h, m] = w.opensAt.split(':').map(Number);
      return { ...w, openMins: h * 60 + m };
    });
    todayWindows.sort((a, b) => a.openMins - b.openMins);
    for (let w of todayWindows) {
      if (w.openMins > minutesSinceMidnight) {
        return { dayOffset: 0, opensAt: w.opensAt };
      }
    }

    for (let offset = 1; offset <= 7; offset++) {
      const nextDay = ((weekday + offset - 1) % 7) + 1;
      const nextWindows = windowsForWeekday(daily, nextDay);
      if (nextWindows.length > 0) {
        const earliest = nextWindows
          .map((w) => {
            const [h, m] = w.opensAt.split(':').map(Number);
            return { opensAt: w.opensAt, mins: h * 60 + m };
          })
          .sort((a, b) => a.mins - b.mins)[0];
        return { dayOffset: offset, opensAt: earliest.opensAt };
      }
    }

    return null;
  };

  const findCurrentClose = (businessHours) => {
    const { timeZone, daily } = businessHours;
    const { weekday, minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const todayWindows = windowsForWeekday(daily, weekday).map((w) => {
      const [hO, mO] = w.opensAt.split(':').map(Number);
      const [hC, mC] = w.closesAt.split(':').map(Number);
      return {
        opensAt: w.opensAt,
        closesAt: w.closesAt,
        openMins: hO * 60 + mO,
        closeMins: hC * 60 + mC,
      };
    });

    for (let w of todayWindows) {
      if (w.closeMins > w.openMins) {
        if (minutesSinceMidnight >= w.openMins && minutesSinceMidnight < w.closeMins) {
          return { dayOffset: 0, closesAt: w.closesAt };
        }
      } else {
        if (minutesSinceMidnight >= w.openMins || minutesSinceMidnight < w.closeMins) {
          const offset = minutesSinceMidnight >= w.openMins ? 0 : 1;
          return { dayOffset: offset, closesAt: w.closesAt };
        }
      }
    }
    return null;
  };

  const computeOpenStatus = () => {
    if (!store?.businessHours || !Array.isArray(store.businessHours.daily)) {
      return { isOpen: false, nextChange: null };
    }
    const bh = store.businessHours;
    const { timeZone } = bh;
    const { minutesSinceMidnight } = getStoreLocalTimeParts(timeZone);

    const currClose = findCurrentClose(bh);
    if (currClose) {
      let [cH, cM] = currClose.closesAt.split(':').map(Number);
      let closeMins = cH * 60 + cM;
      if (currClose.dayOffset === 1) {
        closeMins += 24 * 60;
      }
      const nowMinsAdjusted =
        currClose.dayOffset === 1 && minutesSinceMidnight < closeMins % (24 * 60)
          ? minutesSinceMidnight + 24 * 60
          : minutesSinceMidnight;
      const minutesUntilClose = closeMins - nowMinsAdjusted;
      return {
        isOpen: true,
        nextChange: {
          type: 'closing',
          timeString: to12h(currClose.closesAt),
          minutesUntil: minutesUntilClose,
        },
      };
    }

    const nextOpen = findNextOpen(bh);
    if (!nextOpen) {
      return { isOpen: false, nextChange: null };
    }
    const { dayOffset, opensAt } = nextOpen;
    let [oH, oM] = opensAt.split(':').map(Number);
    let openMins = oH * 60 + oM + dayOffset * 24 * 60;
    const minutesUntilOpen = openMins - minutesSinceMidnight;
    return {
      isOpen: false,
      nextChange: {
        type: 'opening',
        timeString: to12h(opensAt),
        minutesUntil: minutesUntilOpen,
      },
    };
  };

  const hasBusinessHours =
    !!store?.businessHours &&
    Array.isArray(store.businessHours.daily) &&
    store.businessHours.daily.some(({ opensAt, closesAt }) => opensAt !== closesAt);

  const { isOpen, nextChange } = hasBusinessHours
    ? computeOpenStatus()
    : { isOpen: false, nextChange: null };

  let statusText = '';
  if (hasBusinessHours) {
    statusText = 'Closed';
    if (isOpen && nextChange) {
      statusText = `Open until ${nextChange.timeString}`;
    } else if (!isOpen && nextChange) {
      statusText = `Opens at ${nextChange.timeString}`;
    }
  }

  const renderHoursGrid = () => {
    const rows = formatBusinessHoursRows(store.businessHours);
    if (rows.length === 0) {
      return null;
    }

    return rows.map(({ weekdayValue, isToday, dayLabel, dateLabel, timeText }) => (
      <React.Fragment key={weekdayValue}>
        <div className={`day-cell ${isToday ? 'today' : ''}`}>
          {isToday ? 'Today' : dayLabel}
        </div>
        <div className={`date-cell ${isToday ? 'today' : ''}`}>{dateLabel}</div>
        <div className={`time-cell ${isToday ? 'today' : ''}`}>{timeText}</div>
      </React.Fragment>
    ));
  };

  const mapsUrl = () => {
    if (!formattedAddress) return '#';
    const addressString = encodeURIComponent(formattedAddress);
    return `https://www.google.com/maps/search/?api=1&query=${addressString}`;
  };

  const embedUrl = () => {
    if (!formattedAddress) return '';
    const addressString = encodeURIComponent(formattedAddress);
    return `https://www.google.com/maps?q=${addressString}&output=embed`;
  };

  return (
    <div className={`store-detail-container ${loading ? 'loading' : ''}`}>
      {loading && (
        <div className="inline-loading">
          <div className="spinner" />
        </div>
      )}

      {!loading && !store && <p className="not-found">Store not found.</p>}

      {!loading && store && (
        <div>
          {/* ―― STATUS BADGE & STORE NAME (CENTERED with extra top padding) ―― */}
          <div className="header-area">
           
            <h1 className="store-name">{store.name}</h1>
            {hasBusinessHours && (
              <div className={`badge ${isOpen ? 'open' : 'closed'}`}>{statusText}</div>
            )}
          </div>

          {/* ―― CONTACT + MAP SIDE BY SIDE ―― */}
          <div className="contact-map-wrapper">
            {(store.storeContact || store.location) && (
              <div className="card contact-card">
                <h2 className="section-heading">
                  {store.storeContact?.name
                    ? `Contact ${store.storeContact.name}`
                    : 'Contact'}
                </h2>

                {store.storeContact?.phoneNumber && (
                  <p className="contact-info">
                    <span className="material-icons icon">phone</span>
                    <a
                      href={`tel:${(
                        store.storeContact.phoneNumber || ''
                      ).replace(/\D+/g, '')}`}
                      className="link"
                    >
                      {store.storeContact.phoneNumber}
                    </a>
                  </p>
                )}

                {store.storeContact?.email && (
                  <p className="contact-info">
                    <span className="material-icons icon">email</span>
                    <a href={`mailto:${store.storeContact.email}`} className="link">
                      {store.storeContact.email}
                    </a>
                  </p>
                )}

                {formattedAddress && (
                  <p className="contact-info">
                    <span className="material-icons icon">place</span>
                    <a
                      href={mapsUrl()}
                      target="_blank"
                      rel="noopener noreferrer"
                      className="link"
                    >
                      {formattedAddress}
                    </a>
                  </p>
                )}
              </div>
            )}

            {store.location && (
              <div className="card map-card">
                <iframe
                  title="store-map"
                  src={embedUrl()}
                  width="100%"
                  height="100%"
                  style={{ border: 0, borderRadius: '8px' }}
                  allowFullScreen=""
                  loading="lazy"
                />
              </div>
            )}
          </div>

          {store.businessHours && Array.isArray(store.businessHours.daily) && (
            <div className="card hours-card">
              <h2 className="section-heading">Hours</h2>
              <div className="hours-grid">{renderHoursGrid()}</div>
            </div>
          )}
        </div>
      )}

      <style jsx>{`
        .store-detail-container {
          max-width: ${MAX_CONTENT_WIDTH};
          margin: 0 auto;
          padding: 0 16px 48px;
          padding-left: max(16px, env(safe-area-inset-left));
          padding-right: max(16px, env(safe-area-inset-right));
        }

        .store-detail-container.loading {
          display: flex;
          justify-content: center;
          align-items: center;
          height: 100%;
        }
        .inline-loading {
          display: flex;
          justify-content: center;
          align-items: center;
        }
        .spinner {
          width: 40px;
          height: 40px;
          border: 4px solid var(--color-border);
          border-top-color: var(--color-text-primary);
          border-radius: 50%;
          animation: spin 1s linear infinite;
        }
        @keyframes spin {
          to {
            transform: rotate(360deg);
          }
        }

        .not-found {
          text-align: center;
          color: var(--color-text-secondary);
          margin-top: 32px;
          font-size: 1.1rem;
        }

        .header-area {
          display: flex;
          flex-direction: column;
          align-items: center;
          gap: 4px;
          padding-top: 80px;
          padding-bottom: 80px;
        }

        .badge {
          font-size: 1rem;
          font-weight: 600;
          padding: 6px 14px;
          border-radius: 999px;
          text-transform: uppercase;
          display: inline-block;
        }
        .badge.open {
          background-color: var(--color-success-bg);
          color: var(--color-success-text);
        }
        .badge.closed {
          background-color: var(--color-danger-bg);
          color: var(--color-danger-text);
        }

        .store-name {
          margin: 0;
          font-size: ${CODA_EXTRABOLD_DISPLAY_SIZE};
          font-weight: 800;
          font-family: 'Coda', sans-serif;
          color: var(--color-text-primary);
          text-align: center;
          text-transform: uppercase;
        }

        .contact-map-wrapper {
          display: flex;
          gap: 16px;
          margin-bottom: 24px;
        }

        /* Removed box-shadow here */
        .card {
          background-color: var(--color-card-background);
          border: 1px solid var(--color-border);
          border-radius: 8px;
        }

        .contact-card {
          flex: 1;
          padding: 24px 32px;
        }
        .contact-info {
          margin: 8px 0;
          font-size: 1rem;
          display: flex;
          align-items: center;
          gap: 8px;
          color: var(--color-text-primary);
        }
      .contact-info .icon {
    font-size: 1.25rem;
    color: ${COLORS.primary};
  }
       .contact-info .link {
    color: ${COLORS.primary};
    text-decoration: none;
  }
        .contact-info .link:hover {
          text-decoration: underline;
        }

        .map-card {
          flex: 1;
          overflow: hidden;
          height: 200px;
        }

        .hours-card {
          padding: 24px 32px;
        }
        .section-heading {
          margin: 0 0 12px 0;
          font-size: 1.25rem;
          font-weight: 600;
          color: var(--color-text-primary);
          border-bottom: 2px solid var(--color-border);
          padding-bottom: 4px;
        }
        .hours-grid {
          display: grid;
          grid-template-columns: 1fr 1fr 2fr;
          column-gap: 16px;
          row-gap: 8px;
          font-size: 0.95rem;
        }
        .day-cell {
          font-weight: 500;
          color: var(--color-text-primary);
        }
        .date-cell {
          color: var(--color-text-secondary);
        }
        .time-cell {
          color: var(--color-text-secondary);
        }
        .closed-day {
          color: var(--color-badge-muted);
        }
        .today {
          font-weight: 700;
          color: var(--color-text-primary);
        }

        @media (max-width: 768px) {
          .store-name {
            font-size: ${CODA_EXTRABOLD_MOBILE_SIZE};
          }
        }

        @media (max-width: 600px) {
          .contact-map-wrapper {
            flex-direction: column;
          }
          .map-card {
            height: 200px;
          }
          .hours-card {
            padding: 16px 20px;
          }
          .contact-card {
            padding: 16px 20px;
          }
          .hours-grid {
            grid-template-columns: 1fr 1fr 1fr;
          }
        }
      `}</style>
    </div>
  );
}
